import { useAuth, useTheme } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { 
  GraduationCap, 
  Calendar, 
  Bell, 
  Sparkles, 
  CheckCircle2, 
  ArrowRight,
  Sun,
  Moon,
  ChevronRight
} from "lucide-react";
import { motion } from "framer-motion";

const LandingPage = () => {
  const { user, login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const features = [
    {
      icon: GraduationCap,
      title: "Track All Your Schools",
      description: "Add every college you're applying to and keep everything organized in one place."
    },
    {
      icon: Sparkles,
      title: "AI-Powered Research",
      description: "Our AI automatically finds deadlines, requirements, and important info for each school."
    },
    {
      icon: Calendar,
      title: "Visual Calendar",
      description: "See all your deadlines at a glance with our clean calendar view."
    },
    {
      icon: Bell,
      title: "Never Miss a Deadline",
      description: "Get reminders for upcoming deadlines so you're always prepared."
    }
  ];

  const handleGetStarted = () => {
    if (user) {
      navigate("/dashboard");
    } else {
      login();
    }
  };

  return (
    <div className="min-h-screen bg-background noise-bg">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <GraduationCap className="w-8 h-8 text-primary" />
              <span className="font-bold text-xl font-outfit">Admit List</span>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
                data-testid="theme-toggle-btn"
                aria-label="Toggle theme"
              >
                {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
              
              {user ? (
                <Button 
                  onClick={() => navigate("/dashboard")} 
                  className="rounded-full"
                  data-testid="go-to-dashboard-btn"
                >
                  Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={login} 
                  className="rounded-full"
                  data-testid="login-btn"
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              className="text-left"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary text-secondary-foreground text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                AI-Powered College Tracking
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 font-outfit">
                Never miss a college deadline again
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-lg">
                Track applications, get AI-powered insights, and stay organized throughout your college journey.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  onClick={handleGetStarted}
                  className="rounded-full text-base px-8"
                  data-testid="get-started-btn"
                >
                  Get Started Here
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
                  className="rounded-full text-base px-8"
                  data-testid="learn-more-btn"
                >
                  Learn More
                </Button>
              </div>
              
              <div className="flex items-center gap-6 mt-8 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  One-time payment of $19.99
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  Lifetime access
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              className="relative"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl dark:shadow-none">
                <img 
                  src="https://images.unsplash.com/photo-1758270705641-acf09b68a91f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxkaXZlcnNlJTIwZ2VuJTIweiUyMHN0dWRlbnRzJTIwbGF1Z2hpbmclMjBjYW1wdXMlMjBjYW5kaWR8ZW58MHx8fHwxNzczMTYyMjIyfDA&ixlib=rb-4.1.0&q=85"
                  alt="Students on campus"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
              </div>
              
              {/* Floating stats card */}
              <motion.div 
                className="absolute -bottom-6 -left-6 md:left-6"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <Card className="shadow-lg">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <div>
                        <p className="font-mono text-2xl font-bold">99.9%</p>
                        <p className="text-sm text-muted-foreground">On-time submissions</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-24 bg-secondary/30">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 font-outfit">
              Everything you need to stay on track
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From deadline tracking to AI-powered research, we've got your college applications covered.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full feature-card">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2 font-outfit">{feature.title}</h3>
                    <p className="text-muted-foreground text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8">
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              <div className="grid md:grid-cols-2">
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 font-outfit">
                    Ready to get organized?
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Join thousands of students who are crushing their college applications with Admit List.
                  </p>
                  <div>
                    <Button 
                      size="lg"
                      onClick={handleGetStarted}
                      className="rounded-full"
                      data-testid="cta-get-started-btn"
                    >
                      Get Started Here
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </div>
                </div>
                <div className="relative h-64 md:h-auto">
                  <img 
                    src="https://images.unsplash.com/photo-1602409202727-4c6f0ac96bcb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxjb3p5JTIwc3R1ZHklMjBkZXNrJTIwbGFwdG9wJTIwY29mZmVlJTIwbG8tZmklMjBhZXN0aGV0aWN8ZW58MHx8fHwxNzczMTYyMjIwfDA&ixlib=rb-4.1.0&q=85"
                    alt="Cozy study setup"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 md:py-24" id="pricing">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-base md:text-lg font-semibold text-primary mb-2">Simple Pricing</h2>
            <p className="text-2xl md:text-3xl font-bold font-outfit">
              One payment. Lifetime access.
            </p>
          </motion.div>
          
          <motion.div
            className="max-w-md mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <Card className="relative overflow-hidden border-2 border-primary">
              <div className="absolute top-0 left-0 right-0 h-1 bg-primary" />
              <CardContent className="p-8 text-center">
                <h3 className="text-xl font-bold font-outfit mb-2">Admit List</h3>
                <p className="text-muted-foreground mb-6">Everything you need to stay on top of your applications</p>
                <div className="mb-6">
                  <span className="text-5xl font-bold font-outfit">$19.99</span>
                  <span className="text-muted-foreground ml-2">one time</span>
                </div>
                <ul className="text-left space-y-3 mb-8">
                  {[
                    "Unlimited college tracking",
                    "AI-powered deadline research",
                    "Interactive calendar view",
                    "School comparison charts",
                    "Email deadline reminders",
                    "CSV data export",
                    "Share acceptance cards",
                    "Lifetime access — no recurring fees",
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button 
                  size="lg" 
                  onClick={handleGetStarted}
                  className="w-full rounded-full text-base"
                  data-testid="pricing-get-started-btn"
                >
                  Get Started Here
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <GraduationCap className="w-6 h-6 text-primary" />
              <span className="font-semibold font-outfit">Admit List</span>
            </div>
            <div
              data-testid="foundry-badge"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: "'DM Mono', monospace",
                fontSize: '11px',
                letterSpacing: '2px',
                textTransform: 'uppercase',
                color: '#8C7A5A',
                textDecoration: 'none',
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                Forged by
                <span
                  style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontStyle: 'italic',
                    fontSize: '14px',
                    fontWeight: 400,
                    letterSpacing: '0.5px',
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #E8A030 0%, #C8600A 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >The Foundry</span>
              </span>
              <span
                style={{
                  width: '4px',
                  height: '4px',
                  borderRadius: '50%',
                  background: '#C8600A',
                  opacity: 0.6,
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              &copy; {new Date().getFullYear()} Admit List. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
